import { Button, cx, EmptyState, Spinner } from '@freightapi/ui'
import { createRootRoute, Link, Outlet } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'
import { QueueBadge } from '../components/queue/QueueBadge'
import { SIGN_LOCALES, type SignLocale, useSignI18n } from '../lib/locale'
import { setQueueLocale } from '../lib/offline-queue'
import { useIsOnline } from '../lib/online'

/**
 * The shell every screen sits in: a header with the language switch, the offline banner, and the
 * error and not-found boundaries. Width is capped at a phone because that is the only device this
 * app is opened on — a QR code on a consignment note is scanned with a camera, not a laptop.
 */
export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  pendingComponent: RoutePending,
})

function RootLayout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  )
}

function Shell({ children }: { readonly children: ReactNode }) {
  const { t, locale } = useSignI18n()
  const online = useIsOnline()

  // The queue runs outside React and outlives every screen, so the language switch tells it which
  // `Accept-Language` the next submission goes out with rather than the other way around.
  useEffect(() => setQueueLocale(locale), [locale])

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      {/* Chrome, not content: a printed consignment note carries no language switch. */}
      <header className="sticky top-0 z-40 border-line border-b bg-surface print:hidden">
        <div className="mx-auto flex w-full max-w-screen-sm items-center justify-between gap-3 px-4 py-3">
          {/* The name does not wrap: on a 390px header with four languages and a queue badge
              there is no room for a second line, and a header that grows pushes the flow down. */}
          <Link to="/" className="whitespace-nowrap font-semibold text-ink text-lead">
            {t('app.name')}
          </Link>
          <div className="flex items-center gap-1">
            <QueueBadge />
            <LanguageSwitch />
          </div>
        </div>
      </header>

      {online ? null : (
        <p role="status" className="bg-warn/20 px-4 py-2 text-caption text-ink print:hidden">
          {t('offline.body')}
        </p>
      )}

      <main className="mx-auto w-full max-w-screen-sm grow px-4 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] print:max-w-none print:p-0">
        {children}
      </main>
    </div>
  )
}

function LanguageSwitch() {
  const { locale, setLocale, t } = useSignI18n()

  return (
    // A fieldset is the element that carries the "group" role natively; the legend names it for a
    // screen reader while the buttons stay the two-letter codes a driver recognises at a glance.
    <fieldset className="m-0 flex items-center gap-1 border-0 p-0">
      <legend className="sr-only">{t('language.label')}</legend>
      {SIGN_LOCALES.map((option: SignLocale) => (
        <button
          key={option}
          type="button"
          lang={option}
          aria-label={t(`language.${option}`)}
          aria-current={option === locale ? 'true' : undefined}
          onClick={() => setLocale(option)}
          className={cx(
            'min-h-touch rounded-card px-2 font-medium text-body uppercase',
            option === locale ? 'bg-brand-50 text-brand-700' : 'text-muted hover:bg-canvas',
          )}
        >
          {option}
        </button>
      ))}
    </fieldset>
  )
}

function RouteError({ reset }: { readonly reset: () => void }) {
  const { t } = useSignI18n()
  return (
    <Shell>
      <EmptyState
        title={t('error.title')}
        description={t('error.body')}
        action={<Button onClick={reset}>{t('button.retry')}</Button>}
      />
    </Shell>
  )
}

function RouteNotFound() {
  const { t } = useSignI18n()
  return (
    <Shell>
      <EmptyState title={t('error.not_found.title')} description={t('error.not_found.body')} />
    </Shell>
  )
}

function RoutePending() {
  const { t } = useSignI18n()
  return (
    <div className="flex justify-center py-10">
      <Spinner label={t('loading')} size="lg" />
    </div>
  )
}
